import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Col,
  Table,
  Input,
  Button,
  Select,
  Space,
  Row,
  message,
  Tag,
  Tooltip,
  Badge,
  Pagination,
  Checkbox,
  Dropdown,
  Card,
} from 'antd';
import TaskUpdateModal from './TaskUpdateModal';
import TaskDrawerFull from './TaskDrawerFull';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import {
  taskTypes,
  subTypes,
  advertiserTypes,
  urgencyTypes,
  urgencyColors,
  DEFAULT_STATUSES,
  statusTypes,
} from '../configs/task.config';
import { withRouter, Link } from 'react-router-dom';
import qs from 'qs';
import useSearchParams from '../../../../core/hooks/useSearchParams';
import { CloseOutlined, LeftOutlined, RightOutlined, SyncOutlined, QuestionCircleOutlined, DownOutlined, FilterOutlined } from '@ant-design/icons';
import '../styles/SelectInput.css';
import '../styles/CustomTable.css';
import '../styles/CustomPagination.css';
import { debounce } from 'lodash';
import { useOrgContext } from '../../../../core/components/orgs/useOrgContext';
import '../styles/Badge.css';
import { useWindowWidth } from '../../../../core/hooks/useWindowWidth';

const { Option, OptGroup } = Select;

const UnassignedTasksView = ({ history }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [projectFilter, setProjectFilter] = useState('noProject');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const windowWidth = useWindowWidth();
  const {
    fetchTasksPaginated,
    task,
    unassignedTaskPagination,
    setUnassignedTaskPagination,
    tasksLoading,
    updateTask,
    updateTaskLoading,
    currentUser,
  } = useTaskContext();
  const { fetchBasicOrgsDetails, basicOrgsDetails, basicOrgsDetailsLoading } = useOrgContext();
  const unassignedData = task.filter((item) => item.assigneeUser === null);
  const [searchParams, setSearchParams] = useSearchParams({
    requestTypes: '',
    requestSubTypes: '',
    advertiserTypes: '',
    advertiser: '',
    name: '',
    urgency: '',
    status: '',
    showAllStatuses: true,
    showProjectTasks: false,
  });

  const [selectedUpdate, setSelectedUpdate] = useState([]);

  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;

  useEffect(() => {
    handleFetchTasksPaginated({
      skip: (unassignedTaskPagination.current - 1) * unassignedTaskPagination.pageSize || 0,
      first: unassignedTaskPagination.pageSize,
      ...searchParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassignedTaskPagination.pageSize, unassignedTaskPagination.current, searchParams, projectFilter]);

  const debouncedFetchRef = useRef(
    debounce(
      ({
        skip,
        first,
        sortField = null,
        sortOrder = 'updatedAt_DESC',
        name = '',
        requestTypes = '',
        requestSubTypes = '',
        advertiserTypes = '',
        advertiser = '',
        urgency = '',
        status = '',
        showAllStatuses = true,
        showProjectTasks = false,
      }) => {
        const requestTypesArray = requestTypes ? requestTypes.split(',') : [];
        const requestSubTypesArray = requestSubTypes ? requestSubTypes.split(',') : [];
        const advertiserTypesArray = advertiserTypes ? advertiserTypes.split(',') : [];
        const advertiserArray = advertiser ? advertiser.split(',') : [];
        const urgencyArray = urgency ? urgency.split(',') : [];
        const statusArray = status ? status.split(',') : [];
        const orderBy =
          sortOrder === 'updatedAt_DESC'
            ? 'updatedAt_DESC'
            : sortField
              ? `${sortField}_${sortOrder}`
              : 'updatedAt_DESC';

        fetchTasksPaginated({
          variables: {
            where: {
              AND: [
                {
                  name_contains: name,
                  assigneeUser: null,
                  assigneeUsers_every: { id: null },
                },
                ...(requestTypesArray.length ? [{ requestType_in: requestTypesArray }] : []),
                ...(requestSubTypesArray.length ? [{ requestSubType_in: requestSubTypesArray }] : []),
                ...(advertiserTypesArray.length
                  ? [
                      {
                        createdBy: {
                          roles_some: { org: { level_in: advertiserTypesArray } },
                        },
                      },
                    ]
                  : []),
                ...(advertiserArray.length
                  ? [
                      {
                        createdBy: {
                          roles_some: { org: { name_in: advertiserArray } },
                        },
                      },
                    ]
                  : []),
                ...(urgencyArray.length ? [{ urgency_in: urgencyArray }] : []),
                ...(statusArray.length
                  ? [{ status_in: statusArray }]
                  : showAllStatuses === true || showAllStatuses === 'true'
                    ? []
                    : [{ status_in: DEFAULT_STATUSES }]),
                ...(projectFilter !== 'all'
                  ? projectFilter === 'projectOnly'
                    ? [{ NOT: { project: null } }]
                    : [{ project: null }]
                  : []),
              ],
              OR: [
                {
                  createdBy: {
                    roles_some: {
                      org: {
                        OR: [
                          {
                            parentOrg_some: {
                              id: currentUser.role.org.id,
                            },
                          },
                          {
                            id: currentUser.role.org.id,
                          },
                        ],
                      },
                    },
                  },
                },
                {
                  assigneeOrg: {
                    id: currentUser.role.org.id,
                    OR: [
                      { parentOrg_some: { id: currentUser.role.org.id } },
                      { id: currentUser.role.org.id },
                    ],
                  },
                },
              ],
            },
            orderBy,
            skip,
            first,
          },
        });
      },
      500
    )
  );

  const handleFetchTasksPaginated = useCallback(
    (params) => debouncedFetchRef.current(params),
    []
  );

  const fetchAdvertiserOrgs = useCallback(
    debounce((name = '') => {
      fetchBasicOrgsDetails({
        variables: {
          first: 1000,
          skip: 0,
          where: {
            AND: [
              {
                OR: [
                  {
                    assignedTasks_some: {
                      createdBy: {
                        roles_some: {
                          org: {
                            OR: [
                              { id: currentUser.role.org.id },
                              { parentOrg_some: { id: currentUser.role.org.id } },
                            ],
                          },
                        },
                      },
                    },
                  },
                  {
                    channelPartnerTasks_some: {
                      createdBy: {
                        roles_some: {
                          org: {
                            OR: [
                              { id: currentUser.role.org.id },
                              { parentOrg_some: { id: currentUser.role.org.id } },
                            ],
                          },
                        },
                      },
                    },
                  },
                ],
              },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                },
              },
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser.role.org.id, fetchBasicOrgsDetails]
  );

  const handleTaskTypeFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTaskSubTypeFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestSubTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      status: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAdvertiserTypeFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      advertiserTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAdvertiserFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      advertiser: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleUrgencyFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      urgency: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handlePaginationChange = (params) => {
    const updatedPagination = {
      ...unassignedTaskPagination,
      current: params.page || unassignedTaskPagination.current,
      pageSize: params.pageSize || unassignedTaskPagination.pageSize,
    };

    const sortOrder =
      params.sortOrder === 'ascend'
        ? 'ASC'
        : params.sortOrder === 'descend'
          ? 'DESC'
          : 'updatedAt_DESC';

    const sortField =
      sortOrder === 'updatedAt_DESC'
        ? null
        : params.sortField || unassignedTaskPagination.sortField;

    setUnassignedTaskPagination(updatedPagination);

    const newQuery = { ...searchParams, sortField, sortOrder };
    if (sortField === null) {
      newQuery.sortField = undefined;
    }
    const queryString = qs.stringify(newQuery, { addQueryPrefix: true });
    history.push(`/tasks/unassigned/${updatedPagination.current}${queryString}`);

    handleFetchTasksPaginated({
      skip: (updatedPagination.current - 1) * updatedPagination.pageSize || 0,
      first: updatedPagination.pageSize,
      sortField,
      sortOrder,
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const sortField = sorter.field || null;
    const sortOrder = sorter.order || null;

    handlePaginationChange({
      page: 1,
      sortField,
      sortOrder,
    });
  };

  const handlePaginationSizeChange = (current, pageSize) => {
    handlePaginationChange({
      page: 1,
      pageSize,
    });
  };

  const itemRender = (page, type, originalElement) => {
    if (type === 'prev') {
      return (
        <Button icon={<LeftOutlined />} type="text">
          Previous
        </Button>
      );
    }
    if (type === 'next') {
      return (
        <Button type="text">
          Next
          <RightOutlined />
        </Button>
      );
    }
    return originalElement;
  };

  useEffect(() => {
    if (searchParams.advertiser === '') {
      fetchAdvertiserOrgs();
    }
  }, [fetchAdvertiserOrgs, searchParams.advertiser]);

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleUpdate = async (values) => {
    try {
      message.loading('Updating...', 0);
      const { dueDate, status, assigneeOrg, assignee } = values;

      const updatedData = {
        dueDate: dueDate ? dueDate.toISOString() : null,
        status,
        assigneeOrg: assigneeOrg ? { connect: { id: assigneeOrg } } : null,
        assigneeUsers: assignee ? { connect: [{ id: assignee }] } : { disconnectAll: true },
      };

      await updateTask({
        variables: { data: updatedData, where: { id: selectedUpdate.id } },
      });
      setIsModalVisible(false);
      message.destroy();
      message.success('Updated successfully!');
      history.push(`/tasks/main/1`);
    } catch (error) {
      message.destroy();
      message.error('Failed to update.');
      console.error('Failed to update task:', error);
    }
  };

  const handleSearchChange = (name) => {
    const updatedFilters = { ...searchParams, name };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTaskClick = (record) => {
    setSelectedTaskId(record.id);
    setDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedTaskId(null);
  };

  const handleTaskUpdate = useCallback(() => {
    handleFetchTasksPaginated({
      skip: (unassignedTaskPagination.current - 1) * unassignedTaskPagination.pageSize || 0,
      first: unassignedTaskPagination.pageSize,
      ...searchParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, unassignedTaskPagination, handleFetchTasksPaginated]);

  const columns = [
    {
      title: 'Task Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Tooltip title={record.urgency ? `URGENCY: ${record.urgency}` : undefined}>
            <Badge
              dot
              color={urgencyColors[record.urgency] || 'white'}
              className="custom-badge"
              style={{
                marginRight: 0,
                alignSelf: 'center',
              }}
            />
          </Tooltip>
          <Link
            to={`/tasks/details/${record.id}`}
            onClick={(e) => {
              if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                e.preventDefault();
                handleTaskClick(record);
              }
            }}
            style={{
              cursor: 'pointer',
              color: '#1890ff',
              textDecoration: 'none',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}
            onMouseEnter={(e) => {
              e.target.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.target.style.textDecoration = 'none';
            }}
          >
            {text}
          </Link>
        </div>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      sorter: true,
      render: (dueDate, record) => {
        const isPastDue = new Date(dueDate) < new Date();
        return (
          <>
            <span
              style={{
                color: dueDate ? (isPastDue ? 'red' : 'inherit') : 'inherit',
              }}
            >
              {dueDate ? new Date(dueDate).toLocaleDateString('en-CA') : 'N/A'}
            </span>
            {record.recurring && <SyncOutlined style={{ marginLeft: 8, color: 'gray' }} />}
          </>
        );
      },
    },
    {
      title: 'Request Type',
      dataIndex: 'requestType',
      key: 'requestType',
      sorter: true,
      render: (requestType, record) => (
        <>
          {requestType || 'N/A'}
          <br />
          <span style={{ color: 'gray' }}>{record.requestSubType || ''}</span>
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) => {
        if (!status) return '';
        const formattedStatus = status
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Title case each word
          .join(' ');
        return formattedStatus;
      },
    },
    {
      title: 'Advertiser Type',
      dataIndex: 'advertiserType',
      key: 'advertiserType',
      render: (_, record) => {
        const orgLevel =
          record.createdBy &&
          record.createdBy.roles &&
          record.createdBy.roles[0] &&
          record.createdBy.roles[0].org &&
          record.createdBy.roles[0].org.level;
        const formattedOrgLevel = orgLevel
          ? orgLevel
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
          : 'N/A';
        return formattedOrgLevel;
      },
    },
    {
      title: 'Advertiser',
      dataIndex: 'advertiser',
      key: 'advertiser',
      render: (_, record) => {
        const orgName =
          record.createdBy &&
          record.createdBy.roles &&
          record.createdBy.roles[0] &&
          record.createdBy.roles[0].org &&
          record.createdBy.roles[0].org.name;
        return (
          <>
            <Tag
              color="geekblue"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '150px',
              }}
            >
              {orgName || 'N/A'}
            </Tag>
          </>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (_, record) =>
        record.createdAt ? new Date(record.createdAt).toLocaleDateString() : '',
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => {
              setSelectedUpdate(record);
              setIsModalVisible(true);
            }}
          >
            Update
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row
        justify="end"
        style={{
          marginBottom: 16,
          gap: '10px',
          flexDirection: windowWidth < 768 ? 'column' : 'row',
          alignItems: windowWidth < 768 ? 'stretch' : 'center',
        }}
      >
        <Input
          placeholder="Search tasks"
          value={searchParams.name || ''}
          onChange={(e) => {
            handleSearchChange(e.target.value);
          }}
          style={{ width: windowWidth < 768 ? '100%' : 250, height: '40px', borderRadius: '5px' }}
          prefix={
            <Button
              type="text"
              icon={<CloseOutlined style={{ fontSize: '13px' }} />}
              onClick={() =>
                setSearchParams({
                  ...searchParams,
                  name: '',
                })
              }
            />
          }
        />
        <Button
          onClick={() => {
            history.push(`/tasks/form/`);
          }}
          style={{
            fontSize: '15px',
            height: '41px',
            borderRadius: '5px',
            backgroundColor: '#3b82f6',
            color: 'white',
            width: windowWidth < 768 ? '100%' : 'auto',
          }}
        >
          Create Task
        </Button>
      </Row>

      {windowWidth < 768 && (
        <Row style={{ marginTop: 16, paddingBottom: 16 }} wrap>
          <Col span={24}>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                width: '100%',
                height: '45px',
                borderRadius: '5px',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              type={showFilters ? 'default' : 'primary'}
            >
              <FilterOutlined style={{ marginRight: 8 }} />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Col>
        </Row>
      )}

      {(windowWidth >= 768 || showFilters) && (
      <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} wrap>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Request Type</div>
          <Select
            className="custom-select"
            mode="multiple"
            style={{ width: '100%' }}
            onChange={handleTaskTypeFilter}
            value={searchParams.requestTypes ? searchParams.requestTypes.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Request Type"
            allowClear
          >
            {taskTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Sub Type</div>
          <Select
            className="custom-select"
            mode="multiple"
            style={{ width: '100%' }}
            onChange={handleTaskSubTypeFilter}
            value={searchParams.requestSubTypes ? searchParams.requestSubTypes.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Request Subtype"
            allowClear
          >
            {Object.entries(subTypes).map(([mainType, subTypeArray]) => (
              <OptGroup label={mainType} key={mainType}>
                {subTypeArray.map((subType) => (
                  <Option key={`${mainType}-${subType}`} value={subType}>
                    {subType}
                  </Option>
                ))}
              </OptGroup>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Status</div>
          <Select
            mode="multiple"
            className="custom-select"
            style={{ width: '100%' }}
            onChange={handleStatusFilter}
            value={searchParams.status ? searchParams.status.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Status"
            allowClear
          >
            {Object.keys(statusTypes).map((status) => (
              <Option key={status} value={statusTypes[status]}>
                {status}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Advertiser Type</div>
          <Select
            className="custom-select"
            mode="multiple"
            style={{ width: '100%' }}
            onChange={handleAdvertiserTypeFilter}
            value={searchParams.advertiserTypes ? searchParams.advertiserTypes.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Advertiser Type"
            allowClear
          >
            {Object.keys(advertiserTypes).map((type) => (
              <Option key={type} value={advertiserTypes[type]}>
                {type}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Advertiser</div>
          <Select
            mode="multiple"
            className="custom-select"
            showSearch
            style={{ width: '100%' }}
            onChange={handleAdvertiserFilter}
            onSearch={(searchText) => fetchAdvertiserOrgs(searchText)}
            value={searchParams.advertiser ? searchParams.advertiser.split(',') : []}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) => {
              if (optionA.value === 'all') return -1;
              if (optionB.value === 'all') return 1;
              return optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase());
            }}
            loading={basicOrgsDetailsLoading}
            maxTagCount={1}
            placeholder="Filter by Advertiser"
            allowClear
          >
            {basicOrgsDetails.map((org) => (
              <Option key={org.id} value={org.name}>
                {org.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Urgency</div>
          <Select
            className="custom-select"
            mode="multiple"
            style={{ width: '100%' }}
            onChange={handleUrgencyFilter}
            value={searchParams.urgency ? searchParams.urgency.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Urgency"
            allowClear
          >
            {Object.keys(urgencyTypes).map((type) => (
              <Option key={type} value={urgencyTypes[type]}>
                <Badge color={urgencyColors[urgencyTypes[type]]} />
                {type}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row style={{ marginTop: 16, marginBottom: 4 }}>
        <Col>
          <Checkbox
            checked={
              searchParams.showAllStatuses === true || searchParams.showAllStatuses === 'true'
            }
            onChange={(e) => {
              const updatedFilters = {
                ...searchParams,
                showAllStatuses: e.target.checked,
              };
              setSearchParams(updatedFilters);
              setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
            }}
            style={{ fontSize: '15px' }}
          >
            Show all statuses
          </Checkbox>
          <Tooltip
            title={
              <div style={{ maxWidth: 280 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>Unchecked:</strong> Shows only tasks with default statuses
                </div>
                <div style={{ marginBottom: 8, paddingLeft: 12, color: '#a8a8a8' }}>
                  • Not Started <br /> • In Progress <br /> • Ready for Review
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Checked:</strong> Shows tasks with all statuses
                </div>
                <div style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                  Note: Save filters to keep your preferences
                </div>
              </div>
            }
            placement="right"
          >
            <QuestionCircleOutlined
              style={{
                color: '#8c8c8c',
                fontSize: '14px',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Col>
      </Row>
      {level === 'PLATFORM' && (
        <Row style={{ marginTop: 16, marginBottom: 8 }}>
          <Col>
            <Dropdown
              dropdownRender={() => (
                <Card style={{ minWidth: 200 }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '8px',
                      gap: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={projectFilter === 'projectOnly'}
                        onChange={(e) => {
                          const newFilter = e.target.checked ? 'projectOnly' : 'noProject';
                          setProjectFilter(newFilter);
                          setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                      >
                        Show project tasks only
                      </Checkbox>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={projectFilter === 'noProject'}
                        onChange={(e) => {
                          const newFilter = e.target.checked ? 'noProject' : 'projectOnly';
                          setProjectFilter(newFilter);
                          setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                      >
                        Don't show project tasks
                      </Checkbox>
                    </div>
                  </div>
                </Card>
              )}
              trigger={['click']}
              placement="bottomLeft"
            >
              <Button
                style={{
                  height: '35px',
                  borderRadius: '5px',
                }}
              >
                Project Tasks <DownOutlined />
              </Button>
            </Dropdown>
          </Col>
        </Row>
      )}
      </>
      )}

      <Table
        columns={columns}
        dataSource={unassignedData}
        loading={tasksLoading}
        pagination={false}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        className="custom-table"
      />
      <TaskUpdateModal
        visible={isModalVisible}
        onCancel={handleCancel}
        onUpdate={handleUpdate}
        selected={selectedUpdate}
        loading={updateTaskLoading}
      />
      <Pagination
        current={unassignedTaskPagination.current}
        pageSize={unassignedTaskPagination.pageSize}
        total={unassignedTaskPagination.total}
        showSizeChanger
        hideOnSinglePage={false}
        onChange={(page, pageSize) => handlePaginationChange({ page, pageSize })}
        onShowSizeChange={handlePaginationSizeChange}
        style={{ marginTop: 16, paddingBottom: 16 }}
        itemRender={itemRender}
        className="custom-pagination"
        simple={windowWidth < 768}
        size={windowWidth < 768 ? 'small' : 'default'}
      />

      <TaskDrawerFull
        visible={drawerVisible}
        onClose={handleDrawerClose}
        taskId={selectedTaskId}
        onTaskUpdate={handleTaskUpdate}
      />
    </>
  );
};

export default withRouter(UnassignedTasksView);
